import React from "react";
import {
    ENTITY_TRANSACTIONS_THRESHOLD,
    FLAT_RATE_PLANS,
} from "@common/flatRateBilling";
import { SubscriptionType } from "@common/types/billing";
import { useBillingManagement } from "@hooks/useBillingManagement";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { useEffectiveBookkeepingStartDate } from "@hooks/useEffectiveBookkeepingStartDate";
import { ViewTableOverlay } from "../views/ViewTableOverlay";
import { TransactionsFilters } from "./filters/lib";
import { TransactionSortValue } from "./useSort";
import {
    isSortedByDate,
    transactionsMaybeAfterLimitReached,
    transactionsMaybeBeforeBookkeepingStart,
    getHistoricalDataContentHeader,
    getNewDataContentHeader,
    getTransactionLimitReachedContentHeader,
} from "./ctaUtils";

interface UseViewTableOverlayCtaParams {
    filters: TransactionsFilters;
    currentSort: TransactionSortValue;
    currentPage: number;
    totalPages: number;
}

type OverlayCtaResult = [React.ReactNode, React.ReactNode];

interface DateBoundaryOverlayParams extends Pick<
    UseViewTableOverlayCtaParams,
    "currentSort" | "currentPage" | "totalPages"
> {
    topContent: React.ReactNode;
    bottomContent: React.ReactNode;
}

interface SharedPlanOverlayParams extends UseViewTableOverlayCtaParams {
    canManageBilling: boolean;
    currentPlan: FLAT_RATE_PLANS;
    currentUsageLimit: number;
    isFetched: boolean;
    isTrialing: boolean;
    showPastTransactionsCta: boolean;
    showUsageLimitReachedCta: boolean;
    bookkeepingEndDate: Date | null;
    bookkeepingStartDate: Date | null;
}

interface V2PlanOverlayParams extends SharedPlanOverlayParams {
    transactionCount: number;
}

function placeDateBoundaryOverlays({
    topContent,
    bottomContent,
    currentSort,
    currentPage,
    totalPages,
}: DateBoundaryOverlayParams): OverlayCtaResult {
    let prependContent = topContent;
    let appendContent = bottomContent;

    if (currentSort !== TransactionSortValue.DATE_DESC) {
        [prependContent, appendContent] = [appendContent, prependContent];
    }

    return [
        currentPage === 1 ? prependContent : null,
        currentPage === totalPages ? appendContent : null,
    ];
}

function getV1ViewTableOverlayCta({
    filters,
    currentSort,
    currentPage,
    totalPages,
    canManageBilling,
    currentPlan,
    currentUsageLimit,
    isFetched,
    isTrialing,
    showPastTransactionsCta,
    showUsageLimitReachedCta,
    bookkeepingEndDate,
    bookkeepingStartDate,
}: SharedPlanOverlayParams): OverlayCtaResult {
    if (!isSortedByDate(currentSort) || !isFetched || !canManageBilling) {
        return [null, null];
    }

    const showTopContent =
        showUsageLimitReachedCta &&
        transactionsMaybeAfterLimitReached(filters.end, bookkeepingEndDate);

    const showBottomContent =
        showPastTransactionsCta &&
        transactionsMaybeBeforeBookkeepingStart(
            filters.start,
            bookkeepingStartDate,
        );

    return placeDateBoundaryOverlays({
        currentSort,
        currentPage,
        totalPages,
        topContent: showTopContent ? (
            <ViewTableOverlay
                position="top"
                header={getNewDataContentHeader({
                    isTrialing,
                    usageLimit: currentUsageLimit,
                    currentPlan,
                })}
            />
        ) : null,
        bottomContent: showBottomContent ? (
            <ViewTableOverlay
                position="bottom"
                header={getHistoricalDataContentHeader({
                    isTrialing,
                    usageLimit: currentUsageLimit,
                    currentPlan,
                })}
            />
        ) : null,
    });
}

function getV2ViewTableOverlayCta({
    currentPage,
    currentPlan,
    isFetched,
    transactionCount,
}: V2PlanOverlayParams): OverlayCtaResult {
    if (!isFetched) {
        return [null, null];
    }

    const isFreePlan = currentPlan === FLAT_RATE_PLANS.FREE;
    const showTransactionLimitReachedCta =
        isFreePlan && transactionCount >= ENTITY_TRANSACTIONS_THRESHOLD;

    const prependContent = showTransactionLimitReachedCta ? (
        <ViewTableOverlay
            position="top"
            header={getTransactionLimitReachedContentHeader()}
        />
    ) : null;

    return [currentPage === 1 ? prependContent : null, null];
}

export function useViewTableOverlayCta({
    filters,
    currentSort,
    currentPage,
    totalPages,
}: UseViewTableOverlayCtaParams): [React.ReactNode, React.ReactNode] {
    const {
        currentUsageLimit,
        canManageBilling,
        showPastTransactionsCta,
        showUsageLimitReachedCta,
        bookkeepingEndDate,
    } = useBillingManagement();
    const bookkeepingStartDate = useEffectiveBookkeepingStartDate(
        filters.entityIds,
    );

    const {
        currentPlan,
        isTrialing,
        isFetched,
        subscriptionType,
        isV2Plan,
        isV3Plan,
        transactionCount,
    } = useBillingStatus();

    if (subscriptionType !== SubscriptionType.FLAT_RATE) {
        return [null, null];
    }

    const sharedParams = {
        filters,
        currentSort,
        currentPage,
        totalPages,
        canManageBilling,
        currentPlan,
        currentUsageLimit,
        isFetched,
        isTrialing,
        showPastTransactionsCta,
        showUsageLimitReachedCta,
        bookkeepingEndDate,
        bookkeepingStartDate,
    };

    if (isV2Plan || isV3Plan) {
        return getV2ViewTableOverlayCta({
            ...sharedParams,
            transactionCount,
        });
    }

    return getV1ViewTableOverlayCta(sharedParams);
}
